import { ConfigProvider, Listy, Spin, theme } from 'antd';
import React from 'react';

import { cx } from 'config/emotionCss';
import type { MayBe } from 'modules/global/typing/common/common';

import Empty from 'ui/base/Empty';

import {
  getEmptyTextStyle,
  getHeaderStyle,
  getRootStyle,
  HEADER_CLASS,
  ITEM_CLASS,
  LISTY_WRAPPER_CLASS,
  listyItemInlineStyle,
  loadingPlaceholderStyle,
} from './style';
import type { IndexedItem, ListItemProps, ListProps, RowKey } from './types';

function getItemKey<T>({ item, index }: IndexedItem<T>, rowKey?: RowKey<T>): React.Key {
  let key: unknown;
  if (typeof rowKey === 'function') {
    key = rowKey(item);
  } else if (rowKey) {
    key = item[rowKey];
  } else {
    key = (item as MayBe<{ key?: React.Key }>)?.key;
  }

  return (key as MayBe<React.Key>) ?? `list-item-${index}`;
}

const ListItem: React.FC<ListItemProps> = ({ className, style, 'data-cy': dataCy, onClick, children }) => (
  <div className={cx(ITEM_CLASS, className)} style={style} data-cy={dataCy} onClick={onClick}>
    {children}
  </div>
);

/**
 * Transitional stand-in for the deprecated antd `List`, rendered with `Listy`.
 * Mirrors the subset of the `List` API the app uses; delete once every call site uses `Listy` natively.
 */
function List<T>({
  dataSource = [],
  renderItem,
  rowKey,
  loading = false,
  header,
  size = 'default',
  locale,
  className,
  'data-cy': dataCy,
}: ListProps<T>) {
  const { token } = theme.useToken();
  const { renderEmpty } = React.useContext(ConfigProvider.ConfigContext);

  const items = React.useMemo(() => dataSource.map((item, index) => ({ item, index })), [dataSource]);

  const resolveKey = React.useCallback((entry: IndexedItem<T>) => getItemKey(entry, rowKey), [rowKey]);

  const renderEntry = React.useCallback(({ item, index }: IndexedItem<T>) => renderItem(item, index), [renderItem]);

  const renderContent = () => {
    if (items.length > 0) {
      return (
        <Listy
          items={items}
          rowKey={resolveKey}
          itemRender={renderEntry}
          virtual={false}
          classNames={{ item: LISTY_WRAPPER_CLASS }}
          styles={{ item: listyItemInlineStyle }}
        />
      );
    }

    if (loading) {
      return <div className={loadingPlaceholderStyle} />;
    }

    return (
      <div className={getEmptyTextStyle(token)}>
        {locale?.emptyText || renderEmpty?.('List') || <Empty />}
      </div>
    );
  };

  return (
    <div className={cx(getRootStyle(token, size), className)} data-cy={dataCy}>
      {header && <div className={cx(HEADER_CLASS, getHeaderStyle(token))}>{header}</div>}
      <Spin spinning={loading}>{renderContent()}</Spin>
    </div>
  );
}

List.Item = ListItem;

export default List;
